import { useEffect, useState } from "react";
import SEO from "../components/SEO";
import NextPage from "../components/NextPage";
import { Butterfly } from "../components/Butterflies";
import AnimalListing from "../components/AnimalListing";

export default function LookingForNewPastures() {
  const [animals, setAnimals] = useState([]);
  const [inquiryEmail, setInquiryEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, e] = await Promise.all([
          fetch(`${backendUrl}/api/available-animals`).then((r) => r.json()),
          fetch(`${backendUrl}/api/available-animals/inquiry-email`).then((r) => r.json()).catch(() => ({})),
        ]);
        if (cancelled) return;
        setAnimals(Array.isArray(a) ? a : []);
        setInquiryEmail(e?.email || "");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [backendUrl]);

  return (
    <div className="parchment min-h-screen" data-testid="looking-for-new-pastures-page">
      <SEO
        title="Looking for New Pastures"
        description="A few of our Highland cattle are looking for carefully selected new pastures where they will be deeply loved and appreciated."
        path="/looking-for-new-pastures"
      />

      {/* Hero */}
      <section className="relative pt-24 pb-12 sm:pt-32 sm:pb-16 px-6">
        <Butterfly variant="tiny" x="8%" y="18%" delay="0s" scale={0.7} />
        <Butterfly variant="tiny" x="88%" y="26%" delay="1.2s" scale={0.8} />
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-display text-4xl sm:text-6xl text-[#4B3A2F] tracking-[0.12em] leading-tight">
            LOOKING FOR<br className="sm:hidden" /> NEW PASTURES
          </p>
          <div className="gold-rule my-6 max-w-sm mx-auto" />
          <p className="font-script text-2xl sm:text-3xl text-[#8D6E63] leading-relaxed">
            Some chapters are written by welcoming animals home. Others are written by loving them enough to find the place where they can become the whole story.
          </p>
        </div>
      </section>

      {/* Prayerful intro */}
      <section className="max-w-3xl mx-auto px-6 pb-6">
        <p className="font-body text-lg text-[#2C2724]/85 leading-relaxed text-center">
          After much prayer and consideration, The Salty Moo™ is placing greater focus on our beloved Saltie Brays. A few of our Highlands are now looking for carefully selected new pastures where they will be deeply loved and appreciated. This is not a rushed placement. Finding the right homes will always matter more than finding the fastest ones.
        </p>
      </section>

      {/* Listings */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-10">
        {loading ? (
          <p className="text-center font-body text-[#6D5A50]">Loading animals…</p>
        ) : animals.length === 0 ? (
          <p className="text-center font-body text-[#6D5A50] italic">No listings available at the moment.</p>
        ) : (
          animals.map((a) => (
            <AnimalListing key={a.id} animal={a} inquiryEmail={inquiryEmail} />
          ))
        )}
      </section>

      {/* Buyer information */}
      <section className="max-w-3xl mx-auto px-6 pt-6 pb-10">
        <div className="parchment-deep border border-[#A87149]/40 rounded-md p-6 sm:p-8 page-shadow">
          <p className="font-body text-[#2C2724] leading-relaxed">
            Serious inquiries are welcome. Additional photos, videos, records and FaceTime visits are available. Pre-purchase veterinary examinations are welcome at the buyer's expense. Transportation is arranged and paid for by the buyer.
          </p>
        </div>
      </section>

      {/* Closing */}
      <section className="max-w-3xl mx-auto px-6 pb-24 text-center">
        <p className="font-script text-2xl sm:text-3xl text-[#4B3A2F] italic leading-relaxed">
          They may be looking for new pastures, but they will always be part of The Salty Moo™.
        </p>
        <div className="gold-rule my-6 max-w-xs mx-auto opacity-70" />
        <p
          data-testid="creative-rights-notice"
          className="font-body text-xs sm:text-[13px] text-[#6D5A50] leading-relaxed max-w-2xl mx-auto"
        >
          The Salty Moo, LLC retains all rights it owns in its original photographs, writings,
          illustrations, characterizations, storylines, branded identities and other creative
          depictions of these animals, including their use in future books, merchandise and
          promotional materials. Placement transfers ownership of the physical animal only; it
          does not transfer The Salty Moo, LLC's intellectual‑property rights.
        </p>
      </section>

      <NextPage to="/saltie-brays" label="Saltie Brays" />
    </div>
  );
}
